import { useState } from "react";
import Modal from "./Modal";
import type { Deal } from "../../../lib/types";
import {
  completeAdjusterMeeting,
  completeBuild,
  completePreInspection,
} from "../../../lib/dispatchActions";
import { useAuth } from "../../../context/AuthContext";

type EventType = "pre_inspection" | "adjuster_meeting" | "reinspection_adjuster" | "build";

interface Props {
  open: boolean;
  deal: Deal;
  type: EventType;
  onClose: () => void;
  onCompleted: () => void;
}

const OUTCOMES: Array<{ value: string; label: string }> = [
  { value: "approved_full", label: "Approved — full roof" },
  { value: "approved_partial", label: "Approved — partial (reinspection)" },
  { value: "denied", label: "Denied" },
  { value: "rescheduled_by_adjuster", label: "Adjuster rescheduled" },
  { value: "no_show", label: "Adjuster no-show" },
];

export default function CompleteModal({
  open,
  deal,
  type,
  onClose,
  onCompleted,
}: Props) {
  const { member } = useAuth();
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState(OUTCOMES[0].value);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const titleByType: Record<EventType, string> = {
    pre_inspection: "Complete pre-inspection",
    adjuster_meeting: "Complete adjuster meeting",
    reinspection_adjuster: "Complete 2nd adjuster meeting",
    build: "Complete build",
  };

  async function submit() {
    setSubmitting(true);
    setErr(null);
    try {
      if (type === "pre_inspection") {
        await completePreInspection(deal, { findings: notes }, member?.id ?? null);
      } else if (type === "adjuster_meeting") {
        await completeAdjusterMeeting(
          deal,
          { outcome, notes },
          member?.id ?? null,
        );
      } else {
        await completeBuild(deal, { notes }, member?.id ?? null);
      }
      onCompleted();
      onClose();
      setNotes("");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      title={titleByType[type]}
      subtitle={`${deal.homeowner_name ?? "(no name)"} · ${deal.property_street ?? ""}`}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-1.5 rounded bg-bardin-teal text-white text-sm disabled:opacity-60"
          >
            {submitting ? "Saving…" : "Mark complete"}
          </button>
        </>
      }
    >
      {type === "adjuster_meeting" && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Outcome
          </label>
          <select
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          >
            {OUTCOMES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {type === "pre_inspection" ? "Findings" : "Notes"}
          {type === "adjuster_meeting" && (
            <span className="text-slate-400"> (optional)</span>
          )}
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          placeholder={
            type === "pre_inspection"
              ? "Damage observed, slopes affected, photos uploaded to CompanyCam..."
              : type === "adjuster_meeting"
                ? "Key points from the meeting..."
                : "Final build notes, punch list items..."
          }
        />
      </div>

      {err && (
        <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>
      )}
    </Modal>
  );
}
